import { useReducer, useState } from "react";
import {
  FormReducer,
  Action,
  FormDispatch,
  FormStateType,
} from "../Helpers/formTypes";
import { serviceInitialState } from "../Helpers/initialState/serviceInitialState";
import { locationInitialState } from "../Helpers/initialState/locationInitialState";
import { appointmentInitialState } from "../Helpers/initialState/appointmentInitialState";
import { doctorInitialState } from "../Helpers/initialState/doctorInitialState";
import { bookingResponseInitialState } from "../Helpers/initialState/bookingResponseInitialState";
import { pricingSummaryInitialState } from "../Helpers/initialState/pricingSummaryInitialState";
import { missingAppointmentsInitialState } from "../Helpers/initialState/missingAppointmentsInitialState";
import { stepHeadings } from "../stepHeadings";
import { treatmentInitialState } from "../Helpers/initialState/treatmentInitialState";
import { parentTreatmentInitialState } from "../Helpers/initialState/parentTreatmentInitialState";

export const initialFormState: FormStateType = {
  treatment: treatmentInitialState,
  parentTreatment: parentTreatmentInitialState,
  service: serviceInitialState,
  location: locationInitialState,
  doctor: doctorInitialState,
  specializationName: "",
  selectedDay: "",
  selectedMonth: "",
  appointment: appointmentInitialState,
  missingAppointments: missingAppointmentsInitialState,
  bookingResponse: bookingResponseInitialState,
  bookingSessionId: "",
  wasDifferentTimeZoneModalShown: false,
  currentStep: 1,
  remoteContact: {
    label: "",
    appointmentOptions: [],
  },
  pricingSummary: pricingSummaryInitialState,
  relatedServiceSetups: [],
  showRelatedServicesMap: new Map<number, boolean>(),
  showBaseService: true,
  breadcrumbs: [],
};

const reducer = (state: FormStateType, action: Action) => {
  switch (action.type) {
    case FormReducer.update:
      return {
        ...state,
        [action.field]: action.payload,
      };
    case FormReducer.clear:
      return {
        ...state,
        [action.field]: {},
      };
  }
};

export const useFormData = (): {
  state: FormStateType;
  dispatch: FormDispatch;
} => {
  const [state, dispatch] = useReducer(reducer, initialFormState);

  return { state, dispatch };
};

export type StepName =
  | "InitialStep"
  | "TreatmentStep"
  | "AppointmentStep"
  | "PersonalInfoFormStep"
  | "BookingConfirmedStep"

export type Step = {
  stepName: StepName;
  heading: string;
};

export const useFormUI = (): {
  state: FormStateType;
  dispatch: FormDispatch;
  currentStep: number;
  currentStepName: StepName;
  steps: Step[];
  nextStep: () => void;
  previousStep: () => void;
  setStep: (step: number) => void;
  setStepByName: (stepName: StepName) => void;
} => {
  const { state, dispatch } = useFormData();

  const [steps, setSteps] = useState<Step[]>([
    { stepName: "InitialStep", heading: stepHeadings.initialStepHeading },
    { stepName: "TreatmentStep", heading: stepHeadings.treatmentStepHeading },
    { stepName: "AppointmentStep", heading: stepHeadings.appointmentStepHeading },
    { stepName: "PersonalInfoFormStep", heading: stepHeadings.personalInfoFormStepHeading },
    { stepName: "BookingConfirmedStep", heading: stepHeadings.bookingConfirmedStepHeading },
  ]);

  const [currentStep, setCurrentStep] = useState<number>(0);

  const nextStep = () => {
    setCurrentStep((prev) => prev + 1);
  };

  const previousStep = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const setStep = (stepIndex: number) => {
    setCurrentStep(stepIndex);
  };

  const setStepByName = (stepName: StepName) => {
    const stepIndex = steps.findIndex((step) => step.stepName === stepName);
    setCurrentStep(stepIndex);
  };

  return {
    state,
    dispatch,
    currentStep,
    currentStepName: steps[currentStep].stepName,
    steps,
    nextStep,
    previousStep,
    setStep,
    setStepByName,
  };
};
